import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatApiErrorDetail } from '@/lib/api';
import { roleLabel } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, UserCog } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = ['ADMIN', 'FINANCEIRO', 'GERENTE', 'OPERADOR', 'VISUALIZADOR'];

export default function UsersPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'OPERADOR', password: '' });

  const { data } = useQuery({ queryKey: ['users'], queryFn: async () => (await api.get('/users')).data });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post('/users', payload)).data,
    onSuccess: () => { toast.success('Usuário criado'); setOpen(false); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, payload }) => (await api.put(`/users/${id}`, payload)).data,
    onSuccess: () => { toast.success('Atualizado'); qc.invalidateQueries({ queryKey: ['users'] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Equipe</div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Usuários</h1>
        </div>
        <Button onClick={() => { setForm({ email: '', name: '', role: 'OPERADOR', password: '' }); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-2" /> Novo Usuário</Button>
      </div>
      <Card className="border-border/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b"><tr className="text-left text-[10px] uppercase font-mono text-muted-foreground"><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Papel</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Ações</th></tr></thead>
          <tbody>
            {(data || []).map((u) => (
              <tr key={u.id} className="border-b border-border/50">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={(v) => updateMut.mutate({ id: u.id, payload: { role: v } })}>
                    <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <span className={`chip ${u.active ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' : 'bg-zinc-500/10 text-zinc-500 border-zinc-500/30'}`}>{u.active ? 'Ativo' : 'Inativo'}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => updateMut.mutate({ id: u.id, payload: { active: !u.active } })}>{u.active ? 'Desativar' : 'Ativar'}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo usuário</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(form); }} className="space-y-4">
            <div className="space-y-2"><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Senha (mín. 6)</Label><Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Papel</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabel[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">Criar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
